import React, { useEffect, useState } from "react";
import {
  Modal,
  TextField,
  Button,
  Box,
  MenuItem,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { UploadFile as UploadFileIcon } from "@mui/icons-material";
import { useDCS } from "./DCSContext";
import { secondAxios } from "../../../../../../api/axios";
import { addUsers, updateRaci } from "./api";
import { ensureDocumentRevisionFolder } from "../EmsExplorer/sharepointApi";
import { createTask } from "../../../../Project/plant/PlantTaskPage/TaskList/api";
import { formatTaskCodeWithUid } from "../../../../Project/plant/PlantTaskPage/TaskList/components/taskCodeUtils";
import {
  findRegisterDocById,
  findRegisterDocByNumber,
  resolveRegisterDescription,
  resolveRegisterDocNumber,
} from "./raciDocRegisterUtils";

const WBS_CODE_PATTERN = /^\d+(\.\d+)*$/;
const ROLE_LABELS = { acc: "Accountable", cons: "Consulted", inf: "Informed" };
const emptyForm = {
  document: "",
  revNum: "",
  docNum: "",
  registerDocId: "",
  wbsCode: "",
  docLink: "",
  progress: 0,
  responsible: "",
  accountables: [],
  consulted: [],
  informed: [],
  mail: "",
  chat: "",
  timesheet: "",
  file: null,
  projectId: "",
};
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 720,
  maxWidth: "94vw",
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};
// Two-per-row grid for the form body - a field passes `fullRow` to span
// both columns (long/single fields like Description, or the submit row).
const gridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  columnGap: 2,
  rowGap: 1,
};
const fullRowSx = { gridColumn: { xs: "auto", sm: "1 / -1" } };

const NewRowModal = ({
  open,
  handleClose,
  handleAddRow,
  allUsers,
  type,
  plant = false,
  plantProjects = [],
}) => {
  const { documentList, plantId, racis } = useDCS();
  const [formData, setFormData] = useState(emptyForm);
  const [isUploading, setIsUploading] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Snackbar message
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // Snackbar severity (success or error)
  const [openSnackbar, setOpenSnackbar] = useState(false); // Control Snackbar visibility

  // Plant name isn't exposed by DCSContext (only plantId is) - fetched here
  // purely to build the SharePoint plantName/projectName/docNum/Rev N
  // folder path when a RACI is created (see handleSubmit).
  const [plantName, setPlantName] = useState("");

  useEffect(() => {
    if (!plant || !plantId) {
      setPlantName("");
      return;
    }
    let cancelled = false;
    secondAxios
      .get(`/plant/${plantId}`)
      .then((res) => {
        if (!cancelled) setPlantName(res.data?.name || "");
      })
      .catch(() => {
        if (!cancelled) setPlantName("");
      });
    return () => {
      cancelled = true;
    };
  }, [plant, plantId]);

  useEffect(() => {
    setFormData(emptyForm);
    setFileUploaded(false);
    setSnackbarMessage("");
    setOpenSnackbar(false);
  }, [open]);

  // File upload to SharePoint raci
  const uploadFileToSharePointRaci = async (file) => {
    console.log("fileeee", file);

    setIsUploading(true);
    setSnackbarMessage("");
    try {
      const res = await secondAxios.post(
        `/sharepoint/upload/raci`,
        { file },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log("Upload response", res);
      setFormData((prev) => ({
        ...prev,
        docLink: `https://chinookdt189.sharepoint.com/sites/CWT/Shared%20Documents/1. Project%202367%2F1%2E%20Engg%2FPnID%202367%2DRO%2D110%2FREV%2DA%2FDrawing/${file.name}`,
      }));
      setFileUploaded(true);
      setSnackbarMessage("File uploaded successfully!");
      setSnackbarSeverity("success");
    } catch (error) {
      console.error("Error during file upload:", error);
      setSnackbarMessage("Error uploading file. Please try again.");
      setSnackbarSeverity("error");
    } finally {
      setIsUploading(false);
      setOpenSnackbar(true);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setFormData((prev) => ({
        ...prev,
        file: file,
      }));
      uploadFileToSharePointRaci(file);
    } else {
      setSnackbarMessage("Please upload a valid PDF file.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const registerDocs = (documentList || []).filter((doc) => {
    if (!plant || !formData.projectId) return true;
    const rowPid = String(formData.projectId);
    return String(doc?.projectId ?? "") === rowPid;
  });

  // If this document already has RACI revisions (e.g. Rev 0 exists), the
  // next one to create is one past the highest existing numeric revision -
  // so picking the same Document # again auto-suggests Rev 1, Rev 2, etc.
  // instead of repeating whatever the register's own "latest revision" says.
  const nextRevisionForDoc = (docNum) => {
    const existingRevs = (racis || [])
      .filter((r) => r.docNum === docNum)
      .map((r) => parseInt(r.revNum, 10))
      .filter((n) => Number.isFinite(n));
    if (!existingRevs.length) return null;
    return String(Math.max(...existingRevs) + 1);
  };

  const applyRegisterDoc = (selectedDocument) => {
    if (!selectedDocument) {
      setFormData((prev) => ({
        ...prev,
        registerDocId: "",
        docNum: "",
        document: "",
      }));
      return;
    }
    const docNum = resolveRegisterDocNumber(selectedDocument);
    const description = resolveRegisterDescription(selectedDocument);
    const registerRevision = String(
      selectedDocument.revision ?? selectedDocument.revNum ?? "",
    ).trim();
    const suggestedRevision = nextRevisionForDoc(docNum);

    setFormData((prev) => ({
      ...prev,
      registerDocId: String(selectedDocument.id ?? ""),
      docNum,
      document: description,
      revNum: suggestedRevision ?? (registerRevision || prev.revNum),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "registerDocId") {
      const selected =
        findRegisterDocById(documentList, value) ||
        findRegisterDocByNumber(documentList, value);
      applyRegisterDoc(selected);
      return;
    }

    if (name === "projectId") {
      setFormData((prev) => ({
        ...prev,
        projectId: value,
        registerDocId: "",
        docNum: "",
        document: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  // Handle form submission: create the RACI, then (best-effort, each
  // independent of the others) attach Accountable/Consulted/Informed and
  // create/reuse the plantName/projectName/docNum/Rev N SharePoint folder.
  // A failure in either follow-up step doesn't undo the RACI itself - it's
  // surfaced as a warning instead of blocking.
  const handleSubmit = async () => {
    if (
      formData.docNum === "" ||
      formData.revNum === "" ||
      formData.responsible === ""
    ) {
      showSnackbar(
        "You must select Document #, Rev, and Responsible before submitting.",
        "error",
      );
      return;
    }

    if (plant && !formData.projectId) {
      showSnackbar("You must select a WBS before submitting.", "error");
      return;
    }

    const wbsCode = formData.wbsCode.trim();
    if (!wbsCode || !WBS_CODE_PATTERN.test(wbsCode)) {
      showSnackbar("WBS code is required, e.g. 1.1, 1.2, 1.1.1", "error");
      return;
    }

    setSubmitting(true);
    const warnings = [];

    try {
      const created = await handleAddRow({ ...formData, wbsCode });
      const raciId = created?.id;

      if (raciId) {
        const roleAssignments = [
          ["acc", formData.accountables],
          ["cons", formData.consulted],
          ["inf", formData.informed],
        ];
        for (const [roleType, emails] of roleAssignments) {
          if (!emails.length) continue;
          try {
            await addUsers(raciId, roleType, emails);
          } catch (err) {
            warnings.push(`couldn't save ${ROLE_LABELS[roleType]}`);
          }
        }
      } else {
        warnings.push(
          "Accountable/Consulted/Informed weren't attached (couldn't confirm the new RACI's id)",
        );
      }

      if (plant && plantName && formData.projectId) {
        const selectedProject = plantProjects.find(
          (p) => String(p.id || p.name) === String(formData.projectId),
        );
        if (selectedProject?.name) {
          try {
            const folder = await ensureDocumentRevisionFolder({
              plantName,
              projectName: selectedProject.name,
              docNum: formData.docNum,
              revNum: formData.revNum,
            });
            if (raciId && !formData.docLink.trim() && folder?.webUrl) {
              await updateRaci(raciId, { ...formData, docLink: folder.webUrl });
            }
          } catch (err) {
            warnings.push(`couldn't create the Rev ${formData.revNum} folder`);
          }
        }
      }

      // Auto-create the matching 4D Schedule task - no start/end date (the
      // user changes later in the 4D Schedule tab). The backend requires
      // startDate/endDate, so both default to today as a placeholder
      // zero-length span rather than a real estimate. The task's own
      // taskCode already supports a "code[-group]::uid" format (see
      // taskCodeUtils.js); the docNum rides in the ::uid slot purely so a
      // later timesheet entry on this RACI can find its way back to this
      // task (DCSContext.createTimesheetData matches on it) - the 4D
      // Schedule tab itself is plant-scoped, so this only runs in plant mode.
      if (plant && plantId) {
        const responsibleUser = allUsers.find(
          (u) => u.email === formData.responsible,
        );
        const placeholderDate = new Date().toISOString().slice(0, 10);
        try {
          await createTask({
            task: {
              task: `${formData.docNum} - ${formData.document}`.trim(),
              taskCode: formatTaskCodeWithUid({
                code: wbsCode,
                groupName: "",
                uid: formData.docNum,
              }),
              type: "engineering",
              status: "Not Started",
              priority: "Medium",
              startDate: placeholderDate,
              endDate: placeholderDate,
              estimatedTime: 0,
              task_members: responsibleUser?.name || formData.responsible,
            },
            plantId,
          });
        } catch (err) {
          warnings.push("couldn't create the linked 4D Schedule task");
        }
      }

      showSnackbar(
        warnings.length
          ? `RACI created, but ${warnings.join("; ")}.`
          : "RACI created successfully!",
        warnings.length ? "warning" : "success",
      );
      setTimeout(handleClose, warnings.length ? 4000 : 1500);
    } catch (err) {
      showSnackbar(
        err?.response?.data?.message || "Failed to create RACI",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        {documentList ? (
          <>
            <Typography variant="h6" gutterBottom>
              Add New Document
            </Typography>

            <Box sx={gridSx}>
              {/* WBS select (plant mode only) - spans the full row alone
                  when there's no Document # to pair it with yet. */}
              {plant && plantProjects && plantProjects.length > 0 && (
                <TextField
                  fullWidth
                  select
                  label="WBS"
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleChange}
                  required
                >
                  {plantProjects.map((project) => (
                    <MenuItem
                      key={project.id || project.name}
                      value={project.id || project.name}
                    >
                      {project.name || "Unnamed WBS"}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {/* Document # from register */}
              <TextField
                fullWidth
                select
                label="Document #"
                name="registerDocId"
                value={formData.registerDocId}
                onChange={handleChange}
                required
                sx={!(plant && plantProjects?.length > 0) ? fullRowSx : undefined}
                helperText={
                  registerDocs.length === 0
                    ? "Add documents in Document Register first"
                    : "From Document Register"
                }
              >
                {registerDocs.map((d) => {
                  const num = resolveRegisterDocNumber(d);
                  if (!num) return null;
                  const optionValue = String(d.id ?? num);
                  return (
                    <MenuItem key={optionValue} value={optionValue}>
                      {num}
                    </MenuItem>
                  );
                })}
              </TextField>

              <TextField
                fullWidth
                label="Description"
                name="document"
                value={formData.document}
                disabled
                sx={fullRowSx}
                helperText="Set from chosen Document #"
              />

              {/* WBS Code - a hierarchical item number, e.g. 1.1, 1.2,
                  1.1.1 (distinct from the WBS/project select above). */}
              <TextField
                fullWidth
                label="WBS Code"
                name="wbsCode"
                value={formData.wbsCode}
                onChange={handleChange}
                required
                placeholder="1.1"
                helperText="e.g. 1.1, 1.2, 1.1.1"
              />

              {/* RevNum */}
              <TextField
                fullWidth
                label="Rev #"
                name="revNum"
                value={formData.revNum}
                onChange={handleChange}
                required
                helperText={
                  [
                    formData.docNum && nextRevisionForDoc(formData.docNum)
                      ? `Auto-suggested from this document's existing revisions`
                      : null,
                    plant && plantName
                      ? `Creates/reuses "Rev ${formData.revNum || "N"}" under ${plantName}/…/${formData.docNum || "<doc>"}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" — ") || undefined
                }
              />

              {/* Responsible (single, dropdown with all users) */}
              <TextField
                fullWidth
                select
                label="Responsible"
                name="responsible"
                value={formData.responsible}
                onChange={handleChange}
                required
              >
                {allUsers.map((user) => (
                  <MenuItem key={user.email} value={user.email}>
                    {user.name || user.email}
                  </MenuItem>
                ))}
              </TextField>

              {/* Accountable / Consulted / Informed (each multi-select;
                  attached via addUsers() right after the RACI is created,
                  since that's the endpoint the rest of the app already uses
                  for these three roles). Value stays the email (what
                  addUsers/responsible expect); only the display switches to
                  name, once picked, same as the rest of the RACI UI. */}
              {[
                ["accountables", "Accountable"],
                ["consulted", "Consulted"],
                ["informed", "Informed"],
              ].map(([fieldName, label]) => (
                <TextField
                  key={fieldName}
                  fullWidth
                  select
                  label={label}
                  name={fieldName}
                  value={formData[fieldName]}
                  onChange={handleChange}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) =>
                      selected
                        .map(
                          (email) =>
                            allUsers.find((u) => u.email === email)?.name ||
                            email,
                        )
                        .join(", "),
                  }}
                >
                  {allUsers.map((user) => (
                    <MenuItem key={user.email} value={user.email}>
                      {user.name || user.email}
                    </MenuItem>
                  ))}
                </TextField>
              ))}

              {/* Doc Link */}
              <TextField
                fullWidth
                label="Doc Link"
                name="docLink"
                value={formData.docLink}
                onChange={handleChange}
                helperText="Left blank, this fills in from the created Rev folder"
              />

              {/* Progress */}
              <TextField
                fullWidth
                label="Progress"
                name="progress"
                type="number"
                value={formData.progress}
                onChange={handleChange}
              />
            </Box>

            {/* Upload PDF Button */}
            {/* <Box sx={{ mt: 2 }}>
              <input
                accept="application/pdf"
                style={{ display: "none" }}
                id="upload-pdf"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="upload-pdf">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<UploadFileIcon />}
                >
                  Upload PDF
                </Button>
              </label>
              {formData.file && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 1 }}
                >
                  {`Uploaded File: ${formData.file.name}`}
                </Typography>
              )}
            </Box> */}

            {/* {isUploading && <CircularProgress size={24} sx={{ mt: 2 }} />} */}

            {/* Submit Button */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              sx={{ mt: 2 }}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : null}
            >
              {submitting ? "Creating…" : "Add Row"}
            </Button>

            {/* Snackbar for messages */}
            <Snackbar
              open={openSnackbar}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={snackbarSeverity}
                sx={{ width: "100%" }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>
          </>
        ) : (
          <Typography variant="h6" color={"red"}>
            you have to add documents first
          </Typography>
        )}
      </Box>
    </Modal>
  );
};

export default NewRowModal;
